import { Search, X } from "lucide-react";
import { Input } from "~/components/ui/input";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { MultiSelect, MultiSelectOption } from "~/components/MultiSelect";
import { cn } from "~/lib/utils";
import { AssetClassFilter } from "./AssetClassFilter";
import { YieldRangeFilter } from "./YieldRangeFilter";
import { FilterLabel } from "./FilterLabel";
import {
    FILTER_CONTROL_BASE_CLASS,
    FILTER_CONTROL_INTERACTIVE_CLASS,
} from "./filterControlStyles";
import {
    trackFilterSearch,
    trackFilterYieldRange,
    trackFilterRedemption,
    trackFilterNetwork,
    trackFilterNonKyc,
    trackFilterAssetClass,
    trackFilterClearAll,
} from "../../analytics";

// // // //

interface Props {
    searchQuery: string;
    onSearchChange: (value: string) => void;
    yieldRange: [number, number];
    onYieldRangeChange: (value: [number, number]) => void;
    allYields: number[];
    maxYield: number;
    selectedRedemption: string[];
    onRedemptionChange: (value: string[]) => void;
    redemptionOptions: MultiSelectOption[];
    selectedNetworks: string[];
    onNetworksChange: (value: string[]) => void;
    networkOptions: MultiSelectOption[];
    nonKycOnly: boolean;
    onNonKycChange: (value: boolean) => void;
    selectedAssetClasses: string[];
    onAssetClassesChange: (value: string[]) => void;
    assetClassOptions: MultiSelectOption[];
    activeFilterCount: number;
    onClearFilters: () => void;
}

/**
 * Filter controls for the asset screener.
 */
export function AssetScreenerFilters(props: Props) {
    const {
        searchQuery,
        onSearchChange,
        yieldRange,
        onYieldRangeChange,
        allYields,
        maxYield,
        selectedRedemption,
        onRedemptionChange,
        redemptionOptions,
        selectedNetworks,
        onNetworksChange,
        networkOptions,
        nonKycOnly,
        onNonKycChange,
        selectedAssetClasses,
        onAssetClassesChange,
        assetClassOptions,
        activeFilterCount,
        onClearFilters,
    } = props;

    return (
        <div className="space-y-4 text-sm select-none">
            {/* Row 1: Asset Class filter */}
            <AssetClassFilter
                selectedAssetClasses={selectedAssetClasses}
                onAssetClassesChange={(value) => {
                    trackFilterAssetClass(value);
                    onAssetClassesChange(value);
                }}
                assetClassOptions={assetClassOptions}
            />

            {/* Row 2: Search + additional filters */}
            <div className="space-y-4">
                <div className="space-y-1">
                    <FilterLabel label="Filters" />
                    <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-5 lg:gap-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                className={cn(
                                    FILTER_CONTROL_BASE_CLASS,
                                    "h-9 border-border bg-card pl-9 focus-visible:ring-1 focus-visible:ring-blue-300",
                                )}
                                placeholder="Search any asset"
                                value={searchQuery}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    trackFilterSearch(value);
                                    onSearchChange(value);
                                }}
                            />
                        </div>

                        <div>
                            <YieldRangeFilter
                                value={yieldRange}
                                onValueChange={onYieldRangeChange}
                                onApply={trackFilterYieldRange}
                                data={allYields}
                                min={0}
                                max={maxYield}
                            />
                        </div>

                        <div>
                            <MultiSelect
                                options={redemptionOptions}
                                value={selectedRedemption}
                                onChange={(value) => {
                                    trackFilterRedemption(value);
                                    onRedemptionChange(value);
                                }}
                                placeholder="All redemption terms"
                                className={`${FILTER_CONTROL_INTERACTIVE_CLASS} w-full`}
                                maxBadgesShown={3}
                            />
                        </div>

                        <div>
                            <MultiSelect
                                options={networkOptions}
                                value={selectedNetworks}
                                onChange={(value) => {
                                    trackFilterNetwork(value);
                                    onNetworksChange(value);
                                }}
                                placeholder="All networks"
                                className={`${FILTER_CONTROL_INTERACTIVE_CLASS} w-full`}
                                maxBadgesShown={1}
                                searchEnabled
                                searchPlaceholder="Search networks..."
                            />
                        </div>

                        <label
                            className={cn(
                                FILTER_CONTROL_INTERACTIVE_CLASS,
                                "flex h-9 cursor-pointer items-center gap-2 px-3",
                            )}
                        >
                            <Checkbox
                                checked={nonKycOnly}
                                onCheckedChange={(checked) => {
                                    const value = checked === true;
                                    trackFilterNonKyc(value);
                                    onNonKycChange(value);
                                }}
                            />
                            Non-KYC only
                        </label>
                    </div>
                </div>

                {/* Row 3: Clear filters */}
                {activeFilterCount > 0 && (
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-9 shrink-0 cursor-pointer gap-1 px-2 text-sm font-medium text-blue-600 shadow-none hover:bg-transparent hover:text-blue-700"
                            onClick={() => {
                                trackFilterClearAll(activeFilterCount);
                                onClearFilters();
                            }}
                        >
                            <X className="h-4 w-4 shrink-0" />
                            Clear {activeFilterCount} filter
                            {activeFilterCount > 1 ? "s" : ""}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
